import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

import Layout from '../../components/layout/Layout';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Input,
  Label,
  Page,
  PageBody,
  PageHeader,
  Panel,
  PanelBody,
  PanelDescription,
  PanelHeader,
  PanelTitle,
  Spinner,
} from '../../components/ui';
import { useToasts } from '../../stores/useToasts';
import {
  createBrandVoice,
  createCompetitorSet,
  deleteBrandVoice,
  deleteCompetitorSet,
  getApiErrorMessage,
  getBVBootstrapStatus,
  listBrandVoices,
  listCompetitorSets,
  listIndexedBrands,
  seedBVWorkspaceAndIngestion,
  updateBrandVoice,
  updateCompetitorSet,
} from './api';
import BVPageTitle from './components/BVPageTitle';
import BrandVoiceManager from './components/BrandVoiceManager';
import CompetitorSetManager from './components/CompetitorSetManager';
import {
  BVBootstrapSeedRequest,
  BVBootstrapStatus,
  BVBrandVoice,
  BVBrandVoiceDraft,
  BVCompetitorSet,
  BVCompetitorSetDraft,
  BVIndexedBrand,
} from './types';
import { formatDateLabel } from './utils';

const DEFAULT_SEED_REQUEST: BVBootstrapSeedRequest = {
  csv_path: null,
  create_workspace: true,
  replace_existing: false,
  batch_size: 100,
};

type BadgeVariant = 'brand' | 'danger' | 'info' | 'secondary' | 'success' | 'warning';

function formatLabel(value: string | null | undefined): string {
  if (!value) {
    return 'Not available';
  }

  return value.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

function getStatusBadgeVariant(value: string | null | undefined): BadgeVariant {
  switch (value) {
    case 'ready':
    case 'completed':
      return 'success';
    case 'failed':
      return 'danger';
    case 'processing':
    case 'indexing':
    case 'in_review':
      return 'warning';
    case 'queued':
    case 'pending':
      return 'info';
    default:
      return 'secondary';
  }
}

export default function BVReviewsSettingsPage() {
  const { push: addToast } = useToasts();

  const [bootstrapStatus, setBootstrapStatus] = useState<BVBootstrapStatus | null>(null);
  const [brands, setBrands] = useState<BVIndexedBrand[]>([]);
  const [competitorSets, setCompetitorSets] = useState<BVCompetitorSet[]>([]);
  const [brandVoices, setBrandVoices] = useState<BVBrandVoice[]>([]);
  const [seedRequest, setSeedRequest] = useState<BVBootstrapSeedRequest>(DEFAULT_SEED_REQUEST);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  const loadPageData = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }

    try {
      const [nextBootstrapStatus, nextBrands, nextCompetitorSets, nextBrandVoices] = await Promise.all([
        getBVBootstrapStatus(),
        listIndexedBrands(),
        listCompetitorSets(),
        listBrandVoices(),
      ]);

      setBootstrapStatus(nextBootstrapStatus);
      setBrands(nextBrands);
      setCompetitorSets(nextCompetitorSets);
      setBrandVoices(nextBrandVoices);
      setPageError(null);
    } catch (error) {
      setPageError(getApiErrorMessage(error, 'Failed to load Bazaarvoice settings'));
    } finally {
      if (showSpinner) {
        setIsLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadPageData(true);
  }, [loadPageData]);

  const refreshPageData = useCallback(async () => {
    await loadPageData(false);
  }, [loadPageData]);

  const handleSeedWorkspace = async () => {
    setIsSeeding(true);
    try {
      const response = await seedBVWorkspaceAndIngestion(seedRequest);
      addToast({ kind: 'success', message: response.message });
      await refreshPageData();
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to queue BV bootstrap');
      addToast({ kind: 'error', message });
      setPageError(message);
    } finally {
      setIsSeeding(false);
    }
  };

  const handleCreateCompetitorSet = async (payload: BVCompetitorSetDraft) => {
    try {
      await createCompetitorSet(payload);
      addToast({ kind: 'success', message: 'Competitor set created' });
      await refreshPageData();
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to create competitor set');
      addToast({ kind: 'error', message });
      throw new Error(message);
    }
  };

  const handleUpdateCompetitorSet = async (setId: number, payload: BVCompetitorSetDraft) => {
    try {
      await updateCompetitorSet(setId, payload);
      addToast({ kind: 'success', message: 'Competitor set updated' });
      await refreshPageData();
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to update competitor set');
      addToast({ kind: 'error', message });
      throw new Error(message);
    }
  };

  const handleDeleteCompetitorSet = async (setId: number) => {
    try {
      await deleteCompetitorSet(setId);
      addToast({ kind: 'success', message: 'Competitor set deleted' });
      await refreshPageData();
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to delete competitor set');
      addToast({ kind: 'error', message });
      throw new Error(message);
    }
  };

  const handleCreateBrandVoice = async (payload: BVBrandVoiceDraft) => {
    try {
      await createBrandVoice(payload);
      addToast({ kind: 'success', message: 'Brand voice preset created' });
      await refreshPageData();
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to create brand voice preset');
      addToast({ kind: 'error', message });
      throw new Error(message);
    }
  };

  const handleUpdateBrandVoice = async (configId: number, payload: BVBrandVoiceDraft) => {
    try {
      await updateBrandVoice(configId, payload);
      addToast({ kind: 'success', message: 'Brand voice preset updated' });
      await refreshPageData();
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to update brand voice preset');
      addToast({ kind: 'error', message });
      throw new Error(message);
    }
  };

  const handleDeleteBrandVoice = async (configId: number) => {
    try {
      await deleteBrandVoice(configId);
      addToast({ kind: 'success', message: 'Brand voice preset deleted' });
      await refreshPageData();
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to delete brand voice preset');
      addToast({ kind: 'error', message });
      throw new Error(message);
    }
  };

  const ingestionJob = bootstrapStatus?.job ?? null;
  const ingestionProgress = useMemo(() => {
    if (!ingestionJob?.total_rows || !ingestionJob.processed_rows) {
      return null;
    }

    const percent = Math.round((ingestionJob.processed_rows / ingestionJob.total_rows) * 100);
    return Number.isFinite(percent) ? Math.min(percent, 100) : null;
  }, [ingestionJob]);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex h-full items-center justify-center">
          <Spinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Page maxWidth="2xl">
        <PageHeader
          title={<BVPageTitle title="Review Intelligence Settings" />}
          description="Manage competitor baskets, brand voice presets, and the Bazaarvoice workspace bootstrap from one admin surface."
          actions={
            <div className="flex flex-wrap gap-3">
              <Button asChild variant="outline">
                <Link to="/bv-reviews/insights">
                  <ChatBubbleLeftRightIcon className="mr-2 h-4 w-4" />
                  Open Insights
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/bv-reviews/studio">
                  <SparklesIcon className="mr-2 h-4 w-4" />
                  Open Studio
                </Link>
              </Button>
              <Button variant="outline" onClick={() => void refreshPageData()} disabled={isRefreshing}>
                <ArrowPathIcon className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          }
        />
        <PageBody>
          <div className="space-y-6">
            {pageError ? (
              <Alert variant="error" title="Settings load issue">
                {pageError}
              </Alert>
            ) : null}

            <Panel>
              <PanelHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <PanelTitle>Workspace Bootstrap & Ingestion</PanelTitle>
                    <PanelDescription>
                      Validate the seeded BV workspace, inspect the latest ingestion job, and queue a fresh review
                      corpus load when needed.
                    </PanelDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant={getStatusBadgeVariant(bootstrapStatus?.workspace_status)}>
                      Workspace: {formatLabel(bootstrapStatus?.workspace_status)}
                    </Badge>
                    {ingestionJob ? (
                      <Badge variant={getStatusBadgeVariant(ingestionJob.status)}>
                        Job: {formatLabel(ingestionJob.status)}
                      </Badge>
                    ) : (
                      <Badge variant="secondary">No ingestion job yet</Badge>
                    )}
                  </div>
                </div>
              </PanelHeader>
              <PanelBody className="space-y-6">
                {!bootstrapStatus?.default_csv_path_exists ? (
                  <Alert variant="warning" title="Default CSV path is not currently visible">
                    The backend expects the sample dataset at `{bootstrapStatus?.default_csv_path}`. Supply an
                    explicit path below if the container does not have the default file mounted.
                  </Alert>
                ) : null}

                <div className="grid gap-4 lg:grid-cols-4">
                  <div className="rounded-2xl bg-gray-50 p-4 dark:bg-dark-surface-2">
                    <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Workspace</p>
                    <p className="mt-2 text-sm font-medium text-charcoal dark:text-gray-100">
                      {bootstrapStatus?.workspace_display_name || bootstrapStatus?.workspace_name}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {bootstrapStatus?.workspace_exists ? 'Seeded for this tenant' : 'Not created yet'}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-gray-50 p-4 dark:bg-dark-surface-2">
                    <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Vector namespace</p>
                    <p className="mt-2 break-all text-sm font-medium text-charcoal dark:text-gray-100">
                      {bootstrapStatus?.vector_index_namespace || 'Not available'}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-gray-50 p-4 dark:bg-dark-surface-2">
                    <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Latest file</p>
                    <p className="mt-2 text-sm font-medium text-charcoal dark:text-gray-100">
                      {ingestionJob?.filename || 'No file ingested yet'}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {ingestionJob?.created_at ? `Queued ${formatDateLabel(ingestionJob.created_at)}` : 'Awaiting first run'}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-gray-50 p-4 dark:bg-dark-surface-2">
                    <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Indexed brands</p>
                    <p className="mt-2 text-sm font-medium text-charcoal dark:text-gray-100">
                      {brands.length.toLocaleString()}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Distinct brands currently discoverable from the review corpus
                    </p>
                  </div>
                </div>

                {ingestionJob ? (
                  <div className="grid gap-4 lg:grid-cols-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Rows processed</p>
                      <p className="mt-1 text-sm text-charcoal dark:text-gray-100">
                        {(ingestionJob.processed_rows ?? 0).toLocaleString()} / {(ingestionJob.total_rows ?? 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Indexed chunks</p>
                      <p className="mt-1 text-sm text-charcoal dark:text-gray-100">
                        {(ingestionJob.indexed_chunks ?? 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Progress</p>
                      <p className="mt-1 text-sm text-charcoal dark:text-gray-100">
                        {ingestionProgress !== null ? `${ingestionProgress}%` : 'Calculating'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Completed</p>
                      <p className="mt-1 text-sm text-charcoal dark:text-gray-100">
                        {formatDateLabel(ingestionJob.completed_at)}
                      </p>
                    </div>
                    {ingestionJob.error_message ? (
                      <div className="lg:col-span-4">
                        <Alert variant="error" title="Latest ingestion error">
                          {ingestionJob.error_message}
                        </Alert>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_160px]">
                  <div className="space-y-2">
                    <Label htmlFor="bv-bootstrap-csv-path">CSV path override (optional)</Label>
                    <Input
                      id="bv-bootstrap-csv-path"
                      value={seedRequest.csv_path ?? ''}
                      onChange={event =>
                        setSeedRequest(current => ({
                          ...current,
                          csv_path: event.target.value.trim() || null,
                        }))
                      }
                      placeholder={bootstrapStatus?.default_csv_path || '/app/data/reviews.csv'}
                      disabled={isSeeding}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bv-bootstrap-batch-size">Batch size</Label>
                    <Input
                      id="bv-bootstrap-batch-size"
                      type="number"
                      min={1}
                      max={1000}
                      value={seedRequest.batch_size}
                      onChange={event =>
                        setSeedRequest(current => ({
                          ...current,
                          batch_size: Number(event.target.value || DEFAULT_SEED_REQUEST.batch_size),
                        }))
                      }
                      disabled={isSeeding}
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <label className="flex items-start gap-3 rounded-2xl border border-gray-200 px-4 py-3 dark:border-dark-border">
                    <Checkbox
                      checked={seedRequest.create_workspace}
                      onChange={event =>
                        setSeedRequest(current => ({ ...current, create_workspace: event.target.checked }))
                      }
                      disabled={isSeeding}
                    />
                    <div>
                      <p className="text-sm font-medium text-charcoal dark:text-gray-100">Ensure workspace exists</p>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Safe to leave on for reruns; the backend reuses the seeded workspace when it already exists.
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 rounded-2xl border border-gray-200 px-4 py-3 dark:border-dark-border">
                    <Checkbox
                      checked={seedRequest.replace_existing}
                      onChange={event =>
                        setSeedRequest(current => ({ ...current, replace_existing: event.target.checked }))
                      }
                      disabled={isSeeding}
                    />
                    <div>
                      <p className="text-sm font-medium text-charcoal dark:text-gray-100">Replace existing index data</p>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Rebuild the tenant BV vector namespace before indexing the incoming CSV rows.
                      </p>
                    </div>
                  </label>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Default CSV path: {bootstrapStatus?.default_csv_path || 'Not available'}
                  </p>
                  <Button onClick={() => void handleSeedWorkspace()} disabled={isSeeding}>
                    {isSeeding
                      ? 'Queueing...'
                      : bootstrapStatus?.workspace_exists
                        ? 'Run ingestion'
                        : 'Seed workspace + ingest'}
                  </Button>
                </div>
              </PanelBody>
            </Panel>

            <Panel>
              <PanelHeader>
                <PanelTitle>Indexed Brands</PanelTitle>
                <PanelDescription>
                  Distinct brand values aggregated from the ingested review corpus. These power brand selection and
                  competitor curation across the BV applet.
                </PanelDescription>
              </PanelHeader>
              <PanelBody>
                {brands.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {brands.map(brand => (
                      <Badge key={brand.brand} variant="secondary">
                        {brand.brand} ({brand.count.toLocaleString()})
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <Alert variant="info" title="No indexed brands yet">
                    Run the bootstrap or ingestion flow above to populate the distinct brand list from review data.
                  </Alert>
                )}
              </PanelBody>
            </Panel>

            <CompetitorSetManager
              brands={brands}
              items={competitorSets}
              onCreate={handleCreateCompetitorSet}
              onUpdate={handleUpdateCompetitorSet}
              onDelete={handleDeleteCompetitorSet}
            />

            <BrandVoiceManager
              items={brandVoices}
              onCreate={handleCreateBrandVoice}
              onUpdate={handleUpdateBrandVoice}
              onDelete={handleDeleteBrandVoice}
            />
          </div>
        </PageBody>
      </Page>
    </Layout>
  );
}
